import { apiGet, apiPut, apiPost } from './client';
import type { PlanTier } from '@/types';
import type { Block } from '@realsite/blocks';

export interface Template {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  preview_url: string | null;
  thumbnail_url: string | null;
  plan_required: PlanTier;
  locked: boolean;
  is_active: boolean;
  sort_order: number;
  is_applied: boolean;
}

export interface TemplateCustomizations {
  siteName: string;
  tagline?: string;
  location?: string;
  phone?: string;
  email?: string;
  address?: string;
  primaryColor?: string;
  accentColor?: string;
  logoUrl?: string;
}

export interface TemplatesResponse {
  templates: Template[];
}

export async function getTemplates(): Promise<Template[]> {
  const res = await apiGet<TemplatesResponse>('/templates');
  return res.templates;
}

/** A template's HOME-page blocks + style for a live in-wizard preview. For non-block
 *  templates `blocks` is null and `preview_url` (static image) is the fallback. */
export interface TemplatePreview {
  slug: string;
  name: string;
  format: string;
  blocks: Block[] | null;
  style?: Record<string, unknown> | null;
  preview_url?: string | null;
  pages?: Array<{ name: string; slug: string; is_home_page: boolean }>;
}

export async function getTemplatePreview(slug: string): Promise<TemplatePreview> {
  return apiGet<TemplatePreview>(`/templates/${slug}/preview`);
}

export async function applyTemplate(
  tenantId: string,
  templateId: number,
): Promise<{ template_id: number; template_slug: string; template_name: string }> {
  return apiPut(`/tenants/${tenantId}/template`, { template_id: templateId });
}

export interface ApplyTemplateResponse {
  template_slug: string;
  template_name: string;
  format?: string;
  pages?: Array<{ id: string; name: string; slug: string; component_config: unknown; is_home_page: number; sort_order: number }>;
}

export async function applyTemplateBySlug(
  slug: string,
  customizations?: TemplateCustomizations,
): Promise<ApplyTemplateResponse> {
  return apiPost(`/templates/${slug}/apply`, {
    site_name: customizations?.siteName,
    tagline: customizations?.tagline,
    location: customizations?.location,
    phone: customizations?.phone,
    email: customizations?.email,
    address: customizations?.address,
    primary_color: customizations?.primaryColor,
    accent_color: customizations?.accentColor,
    logo_url: customizations?.logoUrl,
    confirm_replace: true,
  });
}
